import { type ReactElement, type KeyboardEvent, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { TdsInlineTabs, TdsInlineTab } from '@scania/tegel-react';
import {
  ResponsiveTable,
  type RowItemBase,
  type ResponsiveTableColumn,
  type OnRowClick,
} from '@optimization/ssi-common';
import { type VehicleCycledEnergyRecord } from '../services/battery-monitoring-api/types/battery-monitoring-api';
import { type SortIndicator } from '../hooks/use-sort-indicator';
import { type UpdateQueryParameterValues } from '../hooks/use-update-query-parameter-values';
import useOnSort from './hooks/use-on-sort';
import styles from './style.module.scss';
import Header from './Header';
import Filters from './Filters';
import FilterMenu from './FilterMenu';
import Toolbar from './utils/Toolbar';
import useOriginalTotalPages from './hooks/use-original-total-pages';
import useCurrentTotalPages from './hooks/use-current-total-pages';
import usePaginatedRows from './hooks/use-paginated-rows';
import useOnPagination from './hooks/use-on-pagination';
import CycledEnergyCell from './utils/CycledEnergyCell';
import CycledEnergyPerKmCell from './utils/CycledEnergyPerKmCell';
import SpecificationCell from './utils/SpecificationCell';
import { renderVehicleTypeCell } from './utils/vehicle-type-cell';
import CalendarAgeCell from './utils/CalendarAgeCell';
import CustomerCell from './utils/CustomerCell';
import { warningColumn } from './utils/warning-column';
import { stateOfHealthColumn } from './utils/state-of-health-column';
import { getOperationalColumns } from './OperationalView';
import VerticalDivider from '@/common/components/VerticalDivider';
import { type SelectedChartSegment } from '@/app/config/chart-segments';

export type VehicleListColumnKey =
  | 'chassisNumber'
  | 'vehicleType'
  | 'specification'
  | 'assemblyDate'
  | 'customer'
  | 'country'
  | 'cycledEnergy'
  | 'cycledEnergyPerKm'
  | 'stateOfHealth'
  | 'topography'
  | 'temperatureMedianC'
  | 'traffic'
  | 'gtwQ3Tonnes'
  | 'dailyDistanceQ3Km'
  | 'totalEcPerKm'
  | 'status';

export interface VehicleRowItem extends RowItemBase {
  vehicle: VehicleCycledEnergyRecord;
}

interface VehicleListProperties {
  readonly paginationValue: number;
  readonly sortIndicator: SortIndicator | null;
  readonly rowsPerPage: number;
  readonly sortedVehiclesCount: number;
  readonly sortedVehicles?: VehicleCycledEnergyRecord[];
  readonly selectedCustomers: string[];
  readonly selectedChassisFilters: string[];
  readonly selectedStateOfHealthSegment: SelectedChartSegment | null;
  readonly selectedCycledEnergySegment: SelectedChartSegment | null;
  readonly selectedBatteries: string[];
  readonly selectedConfigurations: string[];
  readonly selectedVehicleTypes: string[];
  readonly selectedPropulsions: string[];
  readonly batteries: string[];
  readonly configurations: string[];
  readonly showAlerts: boolean;
  readonly openVehicleDetail: OnRowClick<VehicleRowItem>;
  readonly updateQueryParameterValues: UpdateQueryParameterValues;
}

const columnWidths = {
  battery: styles['battery-widths'].split(',').map(Number),
  operational: styles['operational-widths'].split(',').map(Number),
};

const VehicleList = ({
  paginationValue,
  sortIndicator,
  rowsPerPage,
  sortedVehiclesCount,
  sortedVehicles,
  selectedCustomers,
  selectedChassisFilters,
  selectedStateOfHealthSegment,
  selectedCycledEnergySegment,
  selectedBatteries,
  selectedConfigurations,
  selectedVehicleTypes,
  selectedPropulsions,
  batteries,
  configurations,
  showAlerts,
  openVehicleDetail,
  updateQueryParameterValues,
}: VehicleListProperties): ReactElement => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const context = searchParameters.get('view') === 'operational' ? 'operational' : 'battery';
  const setContext = (nextContext: 'battery' | 'operational'): void => {
    if (nextContext === context) return;
    setSearchParameters(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextContext === 'operational') next.set('view', nextContext);
        else next.delete('view');
        return next;
      },
      { preventScrollReset: true },
    );
  };
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = 1 - index;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = 1;
    setContext(nextIndex === 0 ? 'battery' : 'operational');
    event.currentTarget
      .closest('tds-inline-tabs')
      ?.querySelectorAll<HTMLButtonElement>('button[role="tab"]')
      [nextIndex]?.focus({ preventScroll: true });
  };
  const originalTotalPages = useOriginalTotalPages({ filteredRowsCount: sortedVehiclesCount });
  const currentTotalPages = useCurrentTotalPages({ filteredRowsCount: sortedVehiclesCount, rowsPerPage });

  const paginatedVehicles = usePaginatedRows({ paginationValue, rowsPerPage, sortedRows: sortedVehicles });

  const onSort = useOnSort({ sortIndicator, updateQueryParameterValues });
  const onPagination = useOnPagination({ rowsPerPage, updateQueryParameterValues });

  const batteryColumns: ResponsiveTableColumn<VehicleRowItem, VehicleListColumnKey>[] = [
    {
      columnTitle: 'Chassis',
      columnKey: 'chassisNumber',
      sortable: true,
      render: (row: VehicleRowItem) => row.vehicle.chassisNumber,
    },
    {
      columnTitle: 'Type',
      columnKey: 'vehicleType',
      sortable: true,
      render: renderVehicleTypeCell,
    },
    {
      columnTitle: 'Specification',
      columnKey: 'specification',
      sortable: true,
      render: (row: VehicleRowItem) => (
        <SpecificationCell
          configuration={row.vehicle.configuration}
          batteryCapacity={row.vehicle.batteryCapacity}
          socWindow={row.vehicle.socWindow}
        />
      ),
    },
    {
      columnTitle: 'Age',
      columnKey: 'assemblyDate',
      sortable: true,
      render: (row: VehicleRowItem) => (
        <CalendarAgeCell calendarAgeMonths={row.vehicle.calendarAgeMonths} assemblyDate={row.vehicle.assemblyDate} />
      ),
    },
    {
      columnTitle: 'Customer',
      columnKey: 'customer',
      sortable: true,
      render: (row: VehicleRowItem) => <CustomerCell customer={row.vehicle.customer} />,
    },
    {
      columnTitle: 'Country',
      columnKey: 'country',
      sortable: true,
      render: (row: VehicleRowItem) =>
        row.vehicle.customerCountry.trim() || <span className="tds-text-grey-350">N/A</span>,
    },
    {
      columnTitle: 'Cycled Energy',
      textAlign: 'right',
      columnKey: 'cycledEnergy',
      sortable: true,
      render: (row: VehicleRowItem) => (
        <CycledEnergyCell
          cycledEnergyKwh={row.vehicle.cycledEnergyKwh}
          cycledEnergyRatio={row.vehicle.propulsion === 'PHEV' ? undefined : row.vehicle.cycledEnergyRatio}
        />
      ),
    },
    {
      columnTitle: 'Cycled/km',
      textAlign: 'right',
      columnKey: 'cycledEnergyPerKm',
      sortable: true,
      render: (row: VehicleRowItem) => (
        <CycledEnergyPerKmCell
          unavailable={row.vehicle.propulsion === 'PHEV'}
          cycledEnergyPerKmAlert={row.vehicle.cycledEnergyPerKmAlert}
          cycledEnergyPerKm={row.vehicle.cycledEnergyPerKm}
        />
      ),
    },
    stateOfHealthColumn,
    warningColumn,
  ];

  // SCSS exports the same profiles used to constrain header content; Tegel customWidth is only a minimum.
  const widths = columnWidths[context];
  const columns = (context === 'battery' ? batteryColumns : getOperationalColumns()).map((column, index) => ({
    ...column,
    customWidth: `${widths[index]}%`,
    sortingDirection:
      sortIndicator && sortIndicator.columnKey === column.columnKey ? sortIndicator.sortingDirection : undefined,
    disablePadding: true,
    render: (row: VehicleRowItem) => <div className={styles.cell}>{column.render(row)}</div>,
  }));

  const rows: VehicleRowItem[] = useMemo(
    () =>
      paginatedVehicles.map((vehicle) => ({
        vehicle,
      })),
    [paginatedVehicles],
  );

  return (
    <div className={styles.container}>
      <Toolbar>
        <div className={styles.switcher}>
          <TdsInlineTabs
            selectedIndex={context === 'battery' ? 0 : 1}
            leftPadding={0}
            aria-label="Vehicle table view"
            role="tablist"
          >
            <TdsInlineTab>
              <button
                type="button"
                id="battery-view-tab"
                role="tab"
                aria-selected={context === 'battery'}
                aria-controls="vehicle-table"
                tabIndex={context === 'battery' ? 0 : -1}
                onClick={() => setContext('battery')}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.currentTarget.focus({ preventScroll: true });
                }}
                onKeyDown={(event) => onTabKeyDown(event, 0)}
              >
                Battery view
              </button>
            </TdsInlineTab>
            <TdsInlineTab>
              <button
                type="button"
                id="operational-view-tab"
                role="tab"
                aria-selected={context === 'operational'}
                aria-controls="vehicle-table"
                tabIndex={context === 'operational' ? 0 : -1}
                onClick={() => setContext('operational')}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.currentTarget.focus({ preventScroll: true });
                }}
                onKeyDown={(event) => onTabKeyDown(event, 1)}
              >
                Operational view
              </button>
            </TdsInlineTab>
          </TdsInlineTabs>
        </div>
        <div className={styles['vehicle-summary']}>
          <VerticalDivider />
          <div className={styles['vehicle-count']}>
            <Header sortedVehiclesCount={sortedVehiclesCount} />
          </div>
        </div>
        <div className={styles.controls}>
          <div className={styles.filters}>
            <Filters
              selectedCustomers={selectedCustomers}
              selectedChassisFilters={selectedChassisFilters}
              selectedStateOfHealthSegment={selectedStateOfHealthSegment}
              selectedCycledEnergySegment={selectedCycledEnergySegment}
              selectedBatteries={selectedBatteries}
              selectedConfigurations={selectedConfigurations}
              selectedVehicleTypes={selectedVehicleTypes}
              selectedPropulsions={selectedPropulsions}
              updateQueryParameterValues={updateQueryParameterValues}
            />
          </div>
          <div className={styles.actions}>
            <VerticalDivider />
            <FilterMenu
              selectedBatteries={selectedBatteries}
              selectedConfigurations={selectedConfigurations}
              selectedVehicleTypes={selectedVehicleTypes}
              selectedPropulsions={selectedPropulsions}
              batteries={batteries}
              configurations={configurations}
              showAlerts={showAlerts}
              updateQueryParameterValues={updateQueryParameterValues}
            />
          </div>
        </div>
      </Toolbar>
      <div
        id="vehicle-table"
        role="tabpanel"
        aria-labelledby={context === 'battery' ? 'battery-view-tab' : 'operational-view-tab'}
        className={`${styles.table} ${styles[context]}`}
      >
        {rows.length > 0 && (
          <ResponsiveTable
            dataTestid="vehicle-list"
            getRowKey={(row) => row.vehicle.chassisNumber}
            columns={columns}
            rows={rows}
            rowsPerPage={rowsPerPage}
            currentTotalPages={currentTotalPages}
            originalTotalPages={originalTotalPages}
            paginationValue={paginationValue}
            onRowClick={openVehicleDetail}
            onPagination={onPagination}
            onSort={onSort}
          />
        )}
      </div>
    </div>
  );
};

export default VehicleList;
